import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarPlus, ClipboardCheck, MapPin } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TimeInput24 } from "@/components/TimeInput24";
import { todayIso } from "@/lib/dates";

const INPUT = "h-10 w-full rounded-md border bg-background px-3 text-sm";
const STATE = { open: ["Open", "bg-sky-500/20 text-sky-300"], booked: ["Booked", "bg-emerald-500/20 text-emerald-300"] };

const NewSlot = ({ onSaved }) => {
  const [f, setF] = useState({ date: todayIso(), start: "10:00", end: "12:00", location: "", notes: "" });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/assessment-slots", f);
      toast.success("Slot posted. Riders who are due can now book it.");
      setF({ ...f, notes: "" });
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={save} data-testid="new-slot-form" className="grid gap-3 rounded-2xl border bg-card p-5 sm:grid-cols-2 lg:grid-cols-4">
      <label className="space-y-1 text-xs text-muted-foreground">Date<input type="date" min={todayIso()} required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={INPUT} data-testid="slot-date-input" /></label>
      <div className="space-y-1 text-xs text-muted-foreground">From<TimeInput24 value={f.start} onChange={(v) => setF({ ...f, start: v })} testid="slot-start" /></div>
      <div className="space-y-1 text-xs text-muted-foreground">To<TimeInput24 value={f.end} onChange={(v) => setF({ ...f, end: v })} testid="slot-end" /></div>
      <label className="space-y-1 text-xs text-muted-foreground">Meeting point<input required maxLength={200} value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="e.g. Stafford services" className={INPUT} data-testid="slot-location-input" /></label>
      <label className="space-y-1 text-xs text-muted-foreground sm:col-span-2 lg:col-span-3">Notes for the rider (optional)<input maxLength={500} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="e.g. bring licence and kit" className={INPUT} data-testid="slot-notes-input" /></label>
      <button type="submit" data-testid="slot-save-btn" className="inline-flex h-10 items-center justify-center gap-2 self-end rounded-full bg-primary text-sm font-bold text-primary-foreground"><CalendarPlus className="h-4 w-4" /> Post slot</button>
    </form>
  );
};

const SlotRow = ({ s, me, onChange }) => {
  const [label, cls] = STATE[s.status];
  const mine = s.assessor_id === me.id || me.role === "admin";
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); } catch (e) { toast.error(errMsg(e)); } onChange(); };
  return (
    <div data-testid={`slot-row-${s.id}`} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
      <div className="min-w-0 text-sm">
        <div className="flex flex-wrap items-center gap-2 font-semibold">
          {new Date(`${s.date}T12:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })} · {s.start}–{s.end}
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${cls}`}>{label}</span>
        </div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" /> {s.location} · {s.assessor_name}</div>
        {s.status === "booked" && s.seen_by_assessor === false && s.assessor_id === me.id && <span data-testid={`slot-new-${s.id}`} className="rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold uppercase text-white">New booking</span>}
        {s.notes && <div className="text-xs text-muted-foreground">Note: {s.notes}</div>}
        {s.rider_name && <div data-testid={`slot-rider-${s.id}`} className="text-xs font-semibold text-emerald-300">Rider: {s.rider_name}</div>}
      </div>
      <div className="flex flex-wrap gap-2">
        {s.status === "booked" && <Link to={`/assess/new?rider=${s.rider_id}`} data-testid={`slot-startride-${s.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-3 py-1.5 text-xs font-bold text-white"><ClipboardCheck className="h-3.5 w-3.5" /> Start check ride</Link>}
        {mine && <button onClick={() => window.confirm(s.status === "booked" ? `Cancel this slot? ${s.rider_name} will see that it was cancelled.` : "Cancel this slot?") && act(() => api.delete(`/assessment-slots/${s.id}`), "Slot cancelled")} data-testid={`slot-cancel-${s.id}`} className="rounded-full border px-3 py-1.5 text-xs">Cancel</button>}
      </div>
    </div>
  );
};

export default function AssessmentSlots() {
  const { user } = useAuth();
  const [slots, setSlots] = useState(null);
  const load = () => api.get("/assessment-slots").then((r) => { setSlots(r.data); return r.data; });
  useEffect(() => {
    load().then((rows) => api.post("/assessment-slots/mark-seen", { ids: rows.filter((s) => s.seen_by_assessor === false).map((s) => s.id) }))
      .then(() => window.dispatchEvent(new Event("slots-seen"))).catch(() => {});
  }, []);
  const booked = slots?.filter((s) => s.status === "booked").length || 0;
  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">Assessors</p>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tight">Re-assessment slots</h1>
        <p className="mt-1 text-sm text-muted-foreground">Post times you're free. Riders whose check ride is due soon or overdue can book them from My Status.</p>
      </div>
      <NewSlot onSaved={load} />
      <section className="overflow-hidden rounded-2xl border bg-card" data-testid="slot-list">
        <h3 className="border-b px-5 py-4 font-bold">Upcoming slots {slots && <span className="text-sm font-normal text-muted-foreground">· {slots.length} posted, {booked} booked</span>}</h3>
        <div className="divide-y">
          {slots === null ? <div className="h-20 animate-pulse" /> : slots.length === 0 ? <p data-testid="slot-list-empty" className="px-5 py-8 text-sm text-muted-foreground">No upcoming slots. Post one above.</p> : slots.map((s) => <SlotRow key={s.id} s={s} me={user} onChange={load} />)}
        </div>
      </section>
    </div>
  );
}
